import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  ChevronRight,
  ChevronLeft,
  Save,
  Send,
  Edit3,
  Download,
  X
} from 'lucide-react';
import studyAbroadService from '../../services/studyAbroadService';
import PaymentGate from './PaymentGate';

const SOPSection = ({ profile, onRefresh }) => {
  const [mode, setMode] = useState(null); // 'guided' or 'upload'
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    academicJourney: {
      whyThisField: '',
      keyAchievements: [],
      influentialCourses: [],
      challengesOvercome: ''
    },
    professionalExperience: {
      workExperience: [],
      internships: [],
      skills: [],
      leadershipRoles: []
    },
    researchProjects: {
      researchExperience: '',
      significantProjects: [],
      publications: [],
      awards: []
    },
    careerGoals: {
      shortTermGoals: '',
      longTermVision: '',
      programFit: '',
      fieldContribution: ''
    },
    whyThisProgram: {
      facultyInterests: [],
      programFeatures: '',
      resources: '',
      culturalFit: ''
    },
    personalQualities: {
      uniqueBackground: '',
      challengesOvercome: '',
      personalGrowth: '',
      communityInvolvement: []
    },
    additionalInfo: {
      highlights: '',
      gaps: '',
      specialCircumstances: ''
    }
  });
  const [uploadedSOP, setUploadedSOP] = useState(null);
  const [allowReview, setAllowReview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (profile?.sop) {
      if (profile.sop.mode) {
        setMode(profile.sop.mode);
      }
      if (profile.sop.guidedFormData) {
        setFormData(profile.sop.guidedFormData);
      }
      if (profile.sop.uploadedDocument) {
        setUploadedSOP(profile.sop.uploadedDocument);
        setAllowReview(profile.sop.allowReview || false);
      }
    }
  }, [profile]);

  // Check if section is locked (payment not completed or documents not uploaded)
  if (profile?.paymentStatus !== 'paid') {
    return <PaymentGate profile={profile} sectionName="Statement of Purpose" />;
  }

  const steps = [
    {
      title: 'Academic Journey & Motivation',
      description: 'Tell us about your academic background and what drives you',
      section: 'academicJourney'
    },
    {
      title: 'Professional Experience & Skills',
      description: 'Share your work experience and skills developed',
      section: 'professionalExperience'
    },
    {
      title: 'Research & Projects',
      description: 'Highlight your research and significant projects',
      section: 'researchProjects'
    },
    {
      title: 'Career Goals & Aspirations',
      description: 'Describe your career vision and how this program helps',
      section: 'careerGoals'
    },
    {
      title: 'Why This University/Program',
      description: 'Explain why this specific program is right for you',
      section: 'whyThisProgram'
    },
    {
      title: 'Personal Qualities & Experiences',
      description: 'Share what makes you unique',
      section: 'personalQualities'
    },
    {
      title: 'Additional Information',
      description: 'Any other important details',
      section: 'additionalInfo'
    }
  ];

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (section, field, value) => {
    if (!value.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...prev[section][field], value]
      }
    }));
  };

  const handleArrayRemove = (section, field, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSaveProgress = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await studyAbroadService.updateSOPSection(steps[currentStep].section, {
        mode: 'guided',
        data: formData[steps[currentStep].section]
      });

      setSuccessMessage('Progress saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save progress');
      setTimeout(() => setErrorMessage(''), 5000);
      throw error; // Re-throw to prevent navigation on error
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await studyAbroadService.updateSOPSection(steps[currentStep].section, {
        mode: 'guided',
        data: formData[steps[currentStep].section]
      });

      // Only move to next step after successful save
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0); // Scroll to top of new section
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save progress');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      await studyAbroadService.submitSOP();
      
      setSuccessMessage('SOP information submitted successfully! Our team will prepare your statement.');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to submit SOP');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('File size must be less than 5MB');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Only PDF and DOC/DOCX files are allowed');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('sop', file);
      formData.append('allowReview', allowReview);

      const response = await studyAbroadService.uploadSOP(formData);
      
      setUploadedSOP(response.data);
      setSuccessMessage('SOP uploaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to upload SOP');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  // Mode Selection Screen
  if (!mode) {
    return (
      <div className="p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Statement of Purpose</h1>
            <p className="text-gray-600">
              Choose how you'd like to proceed with your Statement of Purpose
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {/* Guided Form Option */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleModeSelect('guided')}
              className="bg-white rounded-lg border-2 border-gray-200 p-8 cursor-pointer hover:border-[#202356] hover:shadow-lg transition-all"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Edit3 className="text-[#202356]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Help Me Write</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Answer guided questions and we'll craft a compelling SOP for you
                </p>
                <ul className="text-xs text-gray-500 space-y-2 text-left w-full">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Step-by-step guidance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Professional SOP writing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Tailored to your story</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Upload Option */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleModeSelect('upload')}
              className="bg-white rounded-lg border-2 border-gray-200 p-8 cursor-pointer hover:border-[#202356] hover:shadow-lg transition-all"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Upload className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">I Have One</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload your existing SOP and we'll review and refine it
                </p>
                <ul className="text-xs text-gray-500 space-y-2 text-left w-full">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Professional review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Grammar & structure check</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Enhancement suggestions</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Upload Mode
  if (mode === 'upload') {
    return (
      <div className="p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => setMode(null)}
              className="text-sm text-gray-600 hover:text-[#202356] mb-4 flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Back to options
            </button>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Upload Your SOP</h1>
            <p className="text-gray-600">
              Upload your existing Statement of Purpose for professional review
            </p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
            >
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-green-700">{successMessage}</p>
            </motion.div>
          )}

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
            >
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </motion.div>
          )}

          {/* Upload Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
            {!uploadedSOP ? (
              <>
                <label
                  htmlFor="sop-upload"
                  className="block p-8 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-[#202356] hover:bg-gray-50 transition-all cursor-pointer"
                >
                  <Upload className="mx-auto text-gray-400 mb-4" size={48} />
                  <p className="text-lg font-medium text-gray-900 mb-2">Upload Your SOP</p>
                  <p className="text-sm text-gray-600 mb-4">
                    PDF or DOC/DOCX • Max 5MB
                  </p>
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors">
                    <Upload size={18} />
                    Choose File
                  </div>
                </label>
                <input
                  id="sop-upload"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  disabled={loading}
                />

                <div className="mt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowReview}
                      onChange={(e) => setAllowReview(e.target.checked)}
                      className="mt-1 w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                    />
                    <span className="text-sm text-gray-700">
                      I grant permission for the NOTA team to review and make improvements to my Statement of Purpose
                    </span>
                  </label>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <FileText className="text-green-600" size={24} />
                    <div>
                      <p className="font-medium text-gray-900">{uploadedSOP.fileName}</p>
                      <p className="text-sm text-gray-600">
                        Uploaded {new Date(uploadedSOP.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <a
                    href={uploadedSOP.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#202356] hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Download size={20} />
                  </a>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <strong>Status:</strong> {uploadedSOP.reviewStatus === 'pending' ? 'Under Review' : uploadedSOP.reviewStatus}
                  </p>
                  {allowReview && (
                    <p className="text-xs text-blue-700 mt-2">
                      ✓ Review permission granted
                    </p>
                  )}
                </div>

                <label
                  htmlFor="sop-upload-replace"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Upload size={18} />
                  Replace SOP
                </label>
                <input
                  id="sop-upload-replace"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Guided Form Mode
  const renderGuidedForm = () => {
    const currentStepData = steps[currentStep];
    const sectionData = formData[currentStepData.section] || {};

    return (
      <div className="p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => setMode(null)}
              className="text-sm text-gray-600 hover:text-[#202356] mb-4 flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Back to options
            </button>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Help Me Write My SOP</h1>
            <p className="text-gray-600">
              Answer the questions below and we'll craft a compelling statement for you
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#202356] h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
            >
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-green-700">{successMessage}</p>
            </motion.div>
          )}

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
            >
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </motion.div>
          )}

          {/* Form Content */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-8 mb-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{currentStepData.title}</h2>
              <p className="text-sm text-gray-600">{currentStepData.description}</p>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {/* Academic Journey Section */}
                {currentStepData.section === 'academicJourney' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Why are you interested in this field of study? *
                      </label>
                      <textarea
                        value={sectionData.whyThisField || ''}
                        onChange={(e) => handleInputChange('academicJourney', 'whyThisField', e.target.value)}
                        rows={4}
                        placeholder="Describe what drew you to this field and what motivates you to pursue it..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Key Academic Achievements
                      </label>
                      <ArrayInput
                        items={sectionData.keyAchievements || []}
                        onAdd={(value) => handleArrayAdd('academicJourney', 'keyAchievements', value)}
                        onRemove={(index) => handleArrayRemove('academicJourney', 'keyAchievements', index)}
                        placeholder="e.g., First Class Honours, Dean's List, Research Award..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Influential Courses or Projects
                      </label>
                      <ArrayInput
                        items={sectionData.influentialCourses || []}
                        onAdd={(value) => handleArrayAdd('academicJourney', 'influentialCourses', value)}
                        onRemove={(index) => handleArrayRemove('academicJourney', 'influentialCourses', index)}
                        placeholder="e.g., Advanced Machine Learning, Capstone Project on AI..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Academic Challenges You've Overcome
                      </label>
                      <textarea
                        value={sectionData.challengesOvercome || ''}
                        onChange={(e) => handleInputChange('academicJourney', 'challengesOvercome', e.target.value)}
                        rows={3}
                        placeholder="Describe any significant academic challenges and how you overcame them..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>
                  </>
                )}

                {/* Professional Experience Section */}
                {currentStepData.section === 'professionalExperience' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Work Experience
                      </label>
                      <ArrayInput
                        items={sectionData.workExperience || []}
                        onAdd={(value) => handleArrayAdd('professionalExperience', 'workExperience', value)}
                        onRemove={(index) => handleArrayRemove('professionalExperience', 'workExperience', index)}
                        placeholder="e.g., Software Engineer at Tech Corp (2020-2023)..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Internships & Volunteering
                      </label>
                      <ArrayInput
                        items={sectionData.internships || []}
                        onAdd={(value) => handleArrayAdd('professionalExperience', 'internships', value)}
                        onRemove={(index) => handleArrayRemove('professionalExperience', 'internships', index)}
                        placeholder="e.g., Summer Intern at Google, Volunteer at Code.org..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Key Skills Developed
                      </label>
                      <ArrayInput
                        items={sectionData.skills || []}
                        onAdd={(value) => handleArrayAdd('professionalExperience', 'skills', value)}
                        onRemove={(index) => handleArrayRemove('professionalExperience', 'skills', index)}
                        placeholder="e.g., Python, Project Management, Team Leadership..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Leadership Roles
                      </label>
                      <ArrayInput
                        items={sectionData.leadershipRoles || []}
                        onAdd={(value) => handleArrayAdd('professionalExperience', 'leadershipRoles', value)}
                        onRemove={(index) => handleArrayRemove('professionalExperience', 'leadershipRoles', index)}
                        placeholder="e.g., Team Lead for 5-person development team..."
                      />
                    </div>
                  </>
                )}

                {/* Research & Projects Section */}
                {currentStepData.section === 'researchProjects' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Research Experience
                      </label>
                      <textarea
                        value={sectionData.researchExperience || ''}
                        onChange={(e) => handleInputChange('researchProjects', 'researchExperience', e.target.value)}
                        rows={4}
                        placeholder="Describe your research experience, methodologies used, and outcomes..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Significant Projects
                      </label>
                      <ArrayInput
                        items={sectionData.significantProjects || []}
                        onAdd={(value) => handleArrayAdd('researchProjects', 'significantProjects', value)}
                        onRemove={(index) => handleArrayRemove('researchProjects', 'significantProjects', index)}
                        placeholder="e.g., Built ML model for cancer detection with 95% accuracy..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Publications & Presentations
                      </label>
                      <ArrayInput
                        items={sectionData.publications || []}
                        onAdd={(value) => handleArrayAdd('researchProjects', 'publications', value)}
                        onRemove={(index) => handleArrayRemove('researchProjects', 'publications', index)}
                        placeholder="e.g., Published in IEEE Conference 2023..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Awards & Recognition
                      </label>
                      <ArrayInput
                        items={sectionData.awards || []}
                        onAdd={(value) => handleArrayAdd('researchProjects', 'awards', value)}
                        onRemove={(index) => handleArrayRemove('researchProjects', 'awards', index)}
                        placeholder="e.g., Best Research Paper Award, Hackathon Winner..."
                      />
                    </div>
                  </>
                )}

                {/* Career Goals Section */}
                {currentStepData.section === 'careerGoals' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Short-term Career Goals (Post-Study) *
                      </label>
                      <textarea
                        value={sectionData.shortTermGoals || ''}
                        onChange={(e) => handleInputChange('careerGoals', 'shortTermGoals', e.target.value)}
                        rows={3}
                        placeholder="What do you plan to do immediately after completing this program?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Long-term Career Vision *
                      </label>
                      <textarea
                        value={sectionData.longTermVision || ''}
                        onChange={(e) => handleInputChange('careerGoals', 'longTermVision', e.target.value)}
                        rows={3}
                        placeholder="Where do you see yourself in 5-10 years?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How This Program Fits Your Goals *
                      </label>
                      <textarea
                        value={sectionData.programFit || ''}
                        onChange={(e) => handleInputChange('careerGoals', 'programFit', e.target.value)}
                        rows={3}
                        placeholder="Explain how this specific program will help you achieve your goals..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How You'll Contribute to Your Field
                      </label>
                      <textarea
                        value={sectionData.fieldContribution || ''}
                        onChange={(e) => handleInputChange('careerGoals', 'fieldContribution', e.target.value)}
                        rows={3}
                        placeholder="What impact do you hope to make in your field?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>
                  </>
                )}

                {/* Why This Program Section */}
                {currentStepData.section === 'whyThisProgram' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Faculty or Research Interests *
                      </label>
                      <ArrayInput
                        items={sectionData.facultyInterests || []}
                        onAdd={(value) => handleArrayAdd('whyThisProgram', 'facultyInterests', value)}
                        onRemove={(index) => handleArrayRemove('whyThisProgram', 'facultyInterests', index)}
                        placeholder="e.g., Prof. Smith's work on quantum computing aligns with my interests..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Research specific professors or research groups at your target university
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unique Program Features *
                      </label>
                      <textarea
                        value={sectionData.programFeatures || ''}
                        onChange={(e) => handleInputChange('whyThisProgram', 'programFeatures', e.target.value)}
                        rows={3}
                        placeholder="What specific aspects of this program appeal to you?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Resources & Opportunities
                      </label>
                      <textarea
                        value={sectionData.resources || ''}
                        onChange={(e) => handleInputChange('whyThisProgram', 'resources', e.target.value)}
                        rows={3}
                        placeholder="What resources, labs, or opportunities are you excited about?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cultural & Community Fit
                      </label>
                      <textarea
                        value={sectionData.culturalFit || ''}
                        onChange={(e) => handleInputChange('whyThisProgram', 'culturalFit', e.target.value)}
                        rows={3}
                        placeholder="Why is this university's culture and community right for you?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>
                  </>
                )}

                {/* Personal Qualities Section */}
                {currentStepData.section === 'personalQualities' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Unique Background or Perspective
                      </label>
                      <textarea
                        value={sectionData.uniqueBackground || ''}
                        onChange={(e) => handleInputChange('personalQualities', 'uniqueBackground', e.target.value)}
                        rows={3}
                        placeholder="What unique experiences or perspectives do you bring?..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personal Challenges Overcome
                      </label>
                      <textarea
                        value={sectionData.challengesOvercome || ''}
                        onChange={(e) => handleInputChange('personalQualities', 'challengesOvercome', e.target.value)}
                        rows={3}
                        placeholder="Describe significant personal challenges and how you grew from them..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personal Growth Moments
                      </label>
                      <textarea
                        value={sectionData.personalGrowth || ''}
                        onChange={(e) => handleInputChange('personalQualities', 'personalGrowth', e.target.value)}
                        rows={3}
                        placeholder="Share moments that shaped who you are today..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Community Involvement & Impact
                      </label>
                      <ArrayInput
                        items={sectionData.communityInvolvement || []}
                        onAdd={(value) => handleArrayAdd('personalQualities', 'communityInvolvement', value)}
                        onRemove={(index) => handleArrayRemove('personalQualities', 'communityInvolvement', index)}
                        placeholder="e.g., Founded coding club for underprivileged youth..."
                      />
                    </div>
                  </>
                )}

                {/* Additional Information Section */}
                {currentStepData.section === 'additionalInfo' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Anything Else to Highlight
                      </label>
                      <textarea
                        value={sectionData.highlights || ''}
                        onChange={(e) => handleInputChange('additionalInfo', 'highlights', e.target.value)}
                        rows={3}
                        placeholder="Any other achievements, experiences, or information you'd like to share..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gaps in Education or Employment (if any)
                      </label>
                      <textarea
                        value={sectionData.gaps || ''}
                        onChange={(e) => handleInputChange('additionalInfo', 'gaps', e.target.value)}
                        rows={2}
                        placeholder="Explain any gaps and what you did during that time..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Special Circumstances
                      </label>
                      <textarea
                        value={sectionData.specialCircumstances || ''}
                        onChange={(e) => handleInputChange('additionalInfo', 'specialCircumstances', e.target.value)}
                        rows={2}
                        placeholder="Any special circumstances the admissions committee should know about..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
                      />
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
              Previous
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveProgress}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <Save size={20} />
                Save Progress
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50"
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Send size={20} />
                  Submit for Review
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Array Input Component
  const ArrayInput = ({ items, onAdd, onRemove, placeholder }) => {
    const [inputValue, setInputValue] = useState('');

    const handleAdd = () => {
      if (inputValue.trim()) {
        onAdd(inputValue.trim());
        setInputValue('');
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
          />
          <button
            onClick={handleAdd}
            type="button"
            className="px-4 py-2 bg-[#202356] text-white rounded-lg hover:bg-[#2542D4] transition-colors"
          >
            Add
          </button>
        </div>
        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <span className="text-sm text-gray-900">{item}</span>
                <button
                  onClick={() => onRemove(index)}
                  type="button"
                  className="text-red-600 hover:text-red-700"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return renderGuidedForm();
};

export default SOPSection;
